import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { List } from 'immutable'
import { formatUTC } from '../../../../services/date-time/index'
import Link, { TARGET_BLANK } from '../../../Link/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../Loading/index'
import { AMBASSADORS_PAGE_STATIC_KEY } from '../../../../services/ambassadors/index'
import { selectStaticTextByKey } from '../../../../services/static-text/selectors'
import {
  selectAmbassadorCampaignDetails,
  selectAmbassadorCampaignDetailsProcessing,
  selectAmbassadorCampaignDetailsError,
} from '../../../../services/ambassadors/selectors'
import { selectUserLanguagePrimary } from '../../../../services/user/index'
import { AMBASSADORS_SET_CAMPAIGN_DETAILS } from '../../../../services/ambassadors/actions'

/**
 * Campaign status info
 * @param {Object} props The props
 * @param {String} props.startDate Start date for the current campaign
 * @param {String} props.endDate Start date for the current campaign
 * @param {Boolean} props.active Is the current campaign active?
 * @returns {JSX.Element}
 */
const CampaignDetailsStatus = (props) => {
  const {
    startDate,
    endDate,
    active,
    staticText,
  } = props

  if (!startDate || !endDate) {
    return null
  }

  const language = useSelector(selectUserLanguagePrimary)
  const activeValue = active
    ? staticText.get('campaignDetailsActiveYes', '')
    : staticText.get('campaignDetailsActiveNo', '')

  return (
    <div className="partner-campaign-details-tab__info">
      <div className="partner-campaign-details-tab__start">
        <div className="partner-campaign-details-tab__header">
          {staticText.get('campaignDetailStart', '')}
        </div>
        <div className="partner-campaign-details-tab__value">
          {formatUTC(startDate, language)}
        </div>
      </div>
      <div className="partner-campaign-details-tab__end">
        <div className="partner-campaign-details-tab__header">
          {staticText.get('campaignDetailEnd', '')}
        </div>
        <div className="partner-campaign-details-tab__value">
          {formatUTC(endDate, language)}
        </div>
      </div>
      <div className="partner-campaign-details-tab__active">
        <div className="partner-campaign-details-tab__header">
          {staticText.get('campaignDetailActive', '')}
        </div>
        <div className="partner-campaign-details-tab__value">
          {activeValue}
        </div>
      </div>
    </div>
  )
}

/**
 * List of the associated LPs
 * @param {Object} props The props
 * @param {List} props.landingPages List with the landing pages associated to the current campaign
 * @param {String} props.utmCampaign utm code for the current campaign
 * @returns {JSX.Element}
 */
const AssociatedLPsList = (props) => {
  const { landingPages } = props

  if (List.isList(landingPages) && landingPages.size > 0) {
    return (
      <div className="partner-campaign-details-tab__row">
        {landingPages.map((landing, index) => {
          const url = landing.get('url')

          return (
            <div
              key={`partner-campaign-details-tab__associated-lp-${index}`}
              className="partner-campaign-details-tab__associated-landing"
            >
              <Link
                to={url}
                className="partner-campaign-details-tab__associated-link"
                target={TARGET_BLANK}
              >
                {url.replace('https://www.', '')}
              </Link>
              <div className="partner-campaign-details-tab__associated-type">
                {landing.get('type')}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="partner-campaign-details-tab__row">
      <div>N/A</div>
    </div>
  )
}

/**
 * Container for the associated LPs
 * @param {Object} props The props
 * @returns {JSX.Element}
 */
const CampaignDetailsAssociatedLps = (props) => {
  const { staticText } = props

  return (
    <div className="partner-campaign-details-tab__associated">
      <div className="partner-campaign-details-tab__header">
        {staticText.get('campaignDetailsAssociatedLps', '')}
      </div>
      <AssociatedLPsList {...props} />
    </div>
  )
}

/**
 * Utm info
 * @param {Object} props The props
 * @param {String} props.utmCampaign utm code for the current campaign
 * @returns {JSX.Element}
 */
const CampaignDetailsUTM = (props) => {
  const { utmCampaign, staticText } = props
  return (
    <div className="partner-campaign-details-tab__utm">
      <div className="partner-campaign-details-tab__header">
        {staticText.get('campaignDetailsUtmCampaign', '')}
      </div>
      <div className="partner-campaign-details-tab__row">
        {utmCampaign}
      </div>
    </div>
  )
}

/**
 * Campaign Details tab component
 * @returns {JSX.Element}
 */
const PartnerCampaignDetailsTab = () => {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const campaignData = useSelector(selectAmbassadorCampaignDetails)
  const campaignDetailsError = useSelector(selectAmbassadorCampaignDetailsError)
  const isCampaignDataProcessing = useSelector(selectAmbassadorCampaignDetailsProcessing)
  const dispatch = useDispatch()

  useEffect(() => {
    return () => {
      dispatch({
        type: AMBASSADORS_SET_CAMPAIGN_DETAILS,
        payload: { data: {} },
      })
    }
  }, [])

  if (campaignDetailsError) {
    return (
      <div className="partner-campaign-details-tab">
        <div className="ambassadors-page__error">
          {staticText.get('somethingWentWrong')}
        </div>
      </div>
    )
  }

  if (isCampaignDataProcessing) {
    return (
      <div className="partner-campaign-details-tab">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  return (
    <div className="partner-campaign-details-tab">
      <CampaignDetailsStatus
        startDate={campaignData.get('startDate')}
        endDate={campaignData.get('endDate')}
        active={campaignData.get('active')}
        staticText={staticText}
      />
      <CampaignDetailsAssociatedLps
        landingPages={campaignData.get('landingPages') || List()}
        utmCampaign={campaignData.get('utmCampaign')}
        staticText={staticText}
      />
      <CampaignDetailsUTM
        utmCampaign={campaignData.get('utmCampaign')}
        staticText={staticText}
      />
    </div>
  )
}

export default PartnerCampaignDetailsTab
